import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  MAX_PRIZES_PER_EVENT,
  defaultPrizeLabel,
  type EventCocodePitInput,
  type EventInput,
  type EventStatus,
  type PrizeInput,
} from '@koto-event/shared';
import { api, type EventDetail } from '../lib/api.js';
import { STATUS_LABEL } from './EventListPage.js';

/**
 * イベント作成・編集（提案書 p.9 の管理画面フロー ①〜④）。
 *
 *   ① 基本情報（名称・説明・開催期間・TOP画像）＋ 対象店舗（連携する CocodePit とスタンプコード）
 *   ② 抽選タイプ選択 → 賞設定（最大10項目：名称／画像／説明／本数／外部の特典コード）
 *   ③ 各画面の画像・文言（おめでとう画面／LINE登録後案内画面／ハニトー演出画面）
 *   ④ くじ参加回数ルール → 作成
 */

const STEPS = ['① 基本情報・対象店舗', '② 抽選・賞設定', '③ 画面の画像・文言', '④ 参加ルール・確認'] as const;

/** 画面上の CocodePit 行。スタンプコードは複数をカンマ区切りで入力する */
interface CocodePitRow {
  pcode: string;
  stampCodesText: string;
  placement: string;
  purpose: string;
  /** 以下は表示のみ（タッチ時に記録される値と実績） */
  locationName?: string;
  touchCount?: number;
  entryCount?: number;
}

function emptyPrize(rank: number, isConsolation = false): PrizeInput {
  return {
    rank,
    name: defaultPrizeLabel(rank, isConsolation),
    description: '',
    imageUrl: null,
    stock: isConsolation ? null : 10,
    winRateBp: isConsolation ? null : 500,
    isConsolation,
    externalRewardCode: null,
  };
}

function emptyCocodePit(): CocodePitRow {
  return { pcode: '', stampCodesText: '', placement: '', purpose: '' };
}

function toCocodePitInput(row: CocodePitRow): EventCocodePitInput {
  return {
    pcode: row.pcode.trim(),
    stampCodes: row.stampCodesText.split(/[,、\s]+/).map((s) => s.trim()).filter(Boolean),
    placement: row.placement,
    purpose: row.purpose,
  };
}

/** ISO 文字列 ⇔ datetime-local 入力値（端末のローカル時刻）の変換 */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function EventEditPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [step, setStep] = useState(0);
  const [status, setStatus] = useState<EventStatus>('DRAFT');
  const [settings, setSettings] = useState<EventDetail['cocodePitSettings'] | null>(null);
  const [testTouchEnabled, setTestTouchEnabled] = useState(false);
  const [testTouch, setTestTouch] = useState<{ pcode: string; liffUrl: string; expiresAt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const now = new Date();
  const [event, setEvent] = useState<EventInput>({
    name: '',
    description: '',
    startAt: now.toISOString(),
    endAt: new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    lotteryType: 'ROULETTE',
    entryRule: 'ONCE_PER_DAY',
    requireFriendForReward: true,
    topImageUrl: null,
    topTitle: '',
    topBody: '',
    resultImageUrl: null,
    resultBody: '特典を受け取るには公式LINEの友だち登録が必要です。',
    lineGuideImageUrl: null,
    lineGuideBody: 'この画面をスタッフに提示ください。',
    stampEffectImageUrl: null,
    stampEffectBody: 'ハニトー完成！特典ゲット',
  });
  const [prizes, setPrizes] = useState<PrizeInput[]>([
    emptyPrize(1),
    emptyPrize(2),
    emptyPrize(3),
    emptyPrize(4),
    emptyPrize(5, true),
  ]);
  const [cocodePits, setCocodePits] = useState<CocodePitRow[]>([emptyCocodePit()]);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      try {
        const e = await api.getEvent(id);
        setStatus(e.status);
        setSettings(e.cocodePitSettings);
        setTestTouchEnabled(e.testTouchEnabled);
        setEvent({
          name: e.name,
          description: e.description,
          startAt: e.startAt,
          endAt: e.endAt,
          lotteryType: e.lotteryType,
          entryRule: e.entryRule,
          requireFriendForReward: e.requireFriendForReward,
          topImageUrl: e.topImageUrl,
          topTitle: e.topTitle,
          topBody: e.topBody,
          resultImageUrl: e.resultImageUrl,
          resultBody: e.resultBody,
          lineGuideImageUrl: e.lineGuideImageUrl,
          lineGuideBody: e.lineGuideBody,
          stampEffectImageUrl: e.stampEffectImageUrl,
          stampEffectBody: e.stampEffectBody,
        });
        setPrizes(
          e.prizes.map((p) => ({
            // 既存の賞は ID を付けて送り、差分更新させる（作り直すと過去の抽選結果から賞が外れる）
            id: p.id,
            rank: p.rank,
            name: p.name,
            description: p.description,
            imageUrl: p.imageUrl,
            stock: p.stock,
            winRateBp: p.winRateBp,
            isConsolation: p.isConsolation,
            externalRewardCode: p.externalRewardCode,
          })),
        );
        setCocodePits(
          e.cocodePits.map((c) => ({
            pcode: c.pcode,
            stampCodesText: c.stampCodes.join(', '),
            placement: c.placement,
            purpose: c.purpose,
            locationName: c.locationName,
            touchCount: c.touchCount,
            entryCount: c.entryCount,
          })),
        );
      } catch (err) {
        setError(err instanceof Error ? err.message : 'イベントの取得に失敗しました');
      }
    })();
  }, [id]);

  // 公開中は開催期間・賞・連携 CocodePit を変更できない（サーバー側でも拒否する）
  const locked = status === 'PUBLISHED';

  const updatePrize = (index: number, patch: Partial<PrizeInput>): void => {
    setPrizes((prev) => prev.map((p, i) => (i === index ? { ...p, ...patch } : p)));
  };
  const updateCocodePit = (index: number, patch: Partial<CocodePitRow>): void => {
    setCocodePits((prev) => prev.map((c, i) => (i === index ? { ...c, ...patch } : c)));
  };

  const uploadImage = async (file: File, apply: (url: string) => void): Promise<void> => {
    try {
      apply((await api.uploadMedia(file)).url);
    } catch (e) {
      setError(e instanceof Error ? e.message : '画像のアップロードに失敗しました');
    }
  };

  const issueTestTouch = async (pcode: string): Promise<void> => {
    if (!id) return;
    setError(null);
    try {
      setTestTouch({ pcode, ...(await api.issueTestTouch(id, pcode)) });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'テスト用の入口を発行できませんでした');
    }
  };

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const pits = cocodePits.filter((c) => c.pcode.trim()).map(toCocodePitInput);
      if (isEdit && id) {
        await api.updateEvent(id, event, locked ? undefined : prizes, locked ? undefined : pits);
      } else {
        await api.createEvent(event, prizes, pits);
      }
      navigate('/events');
    } catch (e) {
      setError(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const totalWinRate = prizes.filter((p) => !p.isConsolation).reduce((sum, p) => sum + (p.winRateBp ?? 0), 0);
  const filledPits = cocodePits.filter((c) => c.pcode.trim());
  const pitsWithoutStamp = filledPits.filter((c) => toCocodePitInput(c).stampCodes.length === 0);

  return (
    <>
      <h1>{isEdit ? 'イベントを編集' : 'イベントを作成'}</h1>
      <p className="page-desc">
        公開すると、連携した CocodePit すべてがこのイベントの入口になります。
        {isEdit && `（現在の状態: ${STATUS_LABEL[status] ?? status}）`}
      </p>
      {locked && (
        <p className="error">
          公開中のため、開催期間・賞・対象店舗（CocodePit）は変更できません。変更する場合はイベント一覧で一時停止してください。
        </p>
      )}

      <div className="steps">
        {STEPS.map((label, i) => (
          <button
            key={label}
            type="button"
            className={`step ${i === step ? 'step--active' : ''}`}
            onClick={() => setStep(i)}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <p className="error">{error}</p>}

      {/* --- ① 基本情報・対象店舗 -------------------------------------------- */}
      {step === 0 && (
        <>
          <div className="card">
            <div className="field">
              <label htmlFor="name">イベント名</label>
              <input id="name" value={event.name} onChange={(e) => setEvent({ ...event, name: e.target.value })} />
            </div>

            <div className="field">
              <label htmlFor="desc">説明</label>
              <textarea
                id="desc"
                value={event.description}
                onChange={(e) => setEvent({ ...event, description: e.target.value })}
              />
            </div>

            <div className="row">
              <div className="field">
                <label htmlFor="startAt">開始日時</label>
                <input
                  id="startAt"
                  type="datetime-local"
                  disabled={locked}
                  value={toLocalInput(event.startAt)}
                  onChange={(e) => setEvent({ ...event, startAt: new Date(e.target.value).toISOString() })}
                />
              </div>
              <div className="field">
                <label htmlFor="endAt">終了日時</label>
                <input
                  id="endAt"
                  type="datetime-local"
                  disabled={locked}
                  value={toLocalInput(event.endAt)}
                  onChange={(e) => setEvent({ ...event, endAt: new Date(e.target.value).toISOString() })}
                />
              </div>
            </div>

            <ImageField
              label="TOP画像（タッチ後の最初の画面）"
              value={event.topImageUrl}
              onPick={(file) => void uploadImage(file, (url) => setEvent({ ...event, topImageUrl: url }))}
              onClear={() => setEvent({ ...event, topImageUrl: null })}
            />
          </div>

          <div className="card">
            <h2 style={{ marginTop: 0 }}>対象店舗（連携する CocodePit）</h2>
            <p className="page-desc">
              製造管理番号（端末に印字されている番号）と、その場所で押印するスタンプのコードを登録します。
              スタンプコードは複数ある場合カンマ区切りで入力します。店舗名は最初のタッチ時に CocodePit から取得します。
            </p>
            {settings && (
              <p className="page-desc">
                CocodePit 管理画面の正常時転送先 URL: <code>{settings.okUrl}</code>
                <br />
                失敗時転送先 URL: <code>{settings.ngUrl}</code>（有効期限 {settings.validPeriodMinutes} 分）
              </p>
            )}

            <table>
              <thead>
                <tr>
                  <th>製造管理番号</th>
                  <th>スタンプコード</th>
                  <th>設置場所</th>
                  <th>設置目的</th>
                  <th>店舗名</th>
                  <th className="num">タッチ</th>
                  <th className="num">参加</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {cocodePits.map((c, index) => (
                  <tr key={index}>
                    <td>
                      <input
                        value={c.pcode}
                        disabled={locked}
                        placeholder="B041-00000654"
                        onChange={(e) => updateCocodePit(index, { pcode: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        value={c.stampCodesText}
                        disabled={locked}
                        onChange={(e) => updateCocodePit(index, { stampCodesText: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        value={c.placement}
                        disabled={locked}
                        placeholder="フロント／客室POP 等"
                        onChange={(e) => updateCocodePit(index, { placement: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        value={c.purpose}
                        disabled={locked}
                        onChange={(e) => updateCocodePit(index, { purpose: e.target.value })}
                      />
                    </td>
                    <td>{c.locationName || '—'}</td>
                    <td className="num">{c.touchCount ?? '—'}</td>
                    <td className="num">{c.entryCount ?? '—'}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        disabled={locked}
                        onClick={() => setCocodePits((prev) => prev.filter((_, i) => i !== index))}
                      >
                        削除
                      </button>
                      {/* 保存済みの CocodePit だけ（未保存の行はサーバーに連携がないため発行できない） */}
                      {testTouchEnabled && id && c.touchCount !== undefined && (
                        <button
                          type="button"
                          className="btn btn--ghost"
                          disabled={busy}
                          onClick={() => void issueTestTouch(c.pcode)}
                        >
                          テスト入口
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="actions">
              <button
                type="button"
                className="btn btn--ghost"
                disabled={locked}
                onClick={() => setCocodePits((prev) => [...prev, emptyCocodePit()])}
              >
                CocodePit を追加
              </button>
            </div>
            {testTouch && (
              <p className="page-desc">
                テスト用の入口（{testTouch.pcode}。{new Date(testTouch.expiresAt).toLocaleTimeString()} まで有効）:{' '}
                <a href={testTouch.liffUrl} target="_blank" rel="noreferrer">
                  {testTouch.liffUrl}
                </a>
                <br />
                開発環境だけの機能です。スマートフォンの LINE で開くと、ココデピットにタッチしたときと同じ画面から始まります。
              </p>
            )}
          </div>
        </>
      )}

      {/* --- ② 抽選タイプ・賞設定 -------------------------------------------- */}
      {step === 1 && (
        <>
          <div className="card">
            <div className="field">
              <label htmlFor="lotteryType">抽選タイプ</label>
              <select
                id="lotteryType"
                value={event.lotteryType}
                onChange={(e) => setEvent({ ...event, lotteryType: e.target.value as EventInput['lotteryType'] })}
              >
                <option value="ROULETTE">ルーレット</option>
                <option value="SCRATCH">スクラッチ</option>
              </select>
            </div>
            <p className="page-desc">当選確率の合計: {(totalWinRate / 100).toFixed(2)}%（残りは参加賞になります）</p>
          </div>

          {prizes.map((prize, index) => (
            <div className="card" key={index}>
              <div className="row">
                <div className="field">
                  <label>賞名</label>
                  <input
                    value={prize.name}
                    disabled={locked}
                    onChange={(e) => updatePrize(index, { name: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label>当選本数（空欄で無制限）</label>
                  <input
                    type="number"
                    min={0}
                    value={prize.stock ?? ''}
                    onChange={(e) => updatePrize(index, { stock: e.target.value === '' ? null : Number(e.target.value) })}
                    disabled={locked || prize.isConsolation}
                  />
                </div>
                <div className="field">
                  <label>当選確率（%）</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.01}
                    value={prize.winRateBp === null ? '' : prize.winRateBp / 100}
                    onChange={(e) =>
                      updatePrize(index, {
                        winRateBp: e.target.value === '' ? null : Math.round(Number(e.target.value) * 100),
                      })
                    }
                    disabled={locked || prize.isConsolation}
                  />
                </div>
                <div className="field">
                  <label>外部の特典コード（空欄なら付与しない）</label>
                  <input
                    value={prize.externalRewardCode ?? ''}
                    disabled={locked}
                    placeholder="連携先で定義された特典のコード"
                    onChange={(e) => updatePrize(index, { externalRewardCode: e.target.value.trim() || null })}
                  />
                </div>
              </div>

              <div className="field">
                <label>景品説明</label>
                <textarea
                  value={prize.description}
                  disabled={locked}
                  onChange={(e) => updatePrize(index, { description: e.target.value })}
                />
              </div>

              <ImageField
                label="景品画像"
                value={prize.imageUrl}
                disabled={locked}
                onPick={(file) => void uploadImage(file, (url) => updatePrize(index, { imageUrl: url }))}
                onClear={() => updatePrize(index, { imageUrl: null })}
              />

              <div className="actions">
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, margin: 0 }}>
                  <input
                    type="checkbox"
                    style={{ width: 'auto' }}
                    checked={prize.isConsolation}
                    disabled={locked}
                    onChange={(e) =>
                      updatePrize(index, {
                        isConsolation: e.target.checked,
                        stock: e.target.checked ? null : 10,
                        winRateBp: e.target.checked ? null : 500,
                      })
                    }
                  />
                  参加賞（ハズレの受け皿）
                </label>
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={() => setPrizes((prev) => prev.filter((_, i) => i !== index))}
                  disabled={locked || prizes.length <= 1}
                >
                  削除
                </button>
              </div>
            </div>
          ))}

          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setPrizes((prev) => [...prev, emptyPrize(prev.length + 1)])}
            disabled={locked || prizes.length >= MAX_PRIZES_PER_EVENT}
          >
            賞を追加（最大 {MAX_PRIZES_PER_EVENT} 項目）
          </button>
        </>
      )}

      {/* --- ③ 各画面の画像・文言 -------------------------------------------- */}
      {step === 2 && (
        <>
          <div className="card">
            <h2 style={{ marginTop: 0 }}>イベントTOP画面</h2>
            <div className="field">
              <label>タイトル</label>
              <input value={event.topTitle} onChange={(e) => setEvent({ ...event, topTitle: e.target.value })} />
            </div>
            <div className="field">
              <label>本文</label>
              <textarea value={event.topBody} onChange={(e) => setEvent({ ...event, topBody: e.target.value })} />
            </div>
          </div>

          <div className="card">
            <h2 style={{ marginTop: 0 }}>「おめでとう」画面（当選直後）</h2>
            <div className="field">
              <label>本文</label>
              <textarea value={event.resultBody} onChange={(e) => setEvent({ ...event, resultBody: e.target.value })} />
            </div>
            <ImageField
              label="画像"
              value={event.resultImageUrl}
              onPick={(file) => void uploadImage(file, (url) => setEvent({ ...event, resultImageUrl: url }))}
              onClear={() => setEvent({ ...event, resultImageUrl: null })}
            />
          </div>

          <div className="card">
            <h2 style={{ marginTop: 0 }}>LINE登録後の案内画面</h2>
            <div className="field">
              <label>本文（例：スタッフに見せてください）</label>
              <textarea
                value={event.lineGuideBody}
                onChange={(e) => setEvent({ ...event, lineGuideBody: e.target.value })}
              />
            </div>
            <ImageField
              label="画像"
              value={event.lineGuideImageUrl}
              onPick={(file) => void uploadImage(file, (url) => setEvent({ ...event, lineGuideImageUrl: url }))}
              onClear={() => setEvent({ ...event, lineGuideImageUrl: null })}
            />
          </div>

          <div className="card">
            <h2 style={{ marginTop: 0 }}>ハニトー演出画面（押印後）</h2>
            <div className="field">
              <label>本文</label>
              <textarea
                value={event.stampEffectBody}
                onChange={(e) => setEvent({ ...event, stampEffectBody: e.target.value })}
              />
            </div>
            <ImageField
              label="演出画像"
              value={event.stampEffectImageUrl}
              onPick={(file) => void uploadImage(file, (url) => setEvent({ ...event, stampEffectImageUrl: url }))}
              onClear={() => setEvent({ ...event, stampEffectImageUrl: null })}
            />
          </div>
        </>
      )}

      {/* --- ④ 参加ルール・確認 ---------------------------------------------- */}
      {step === 3 && (
        <div className="card">
          <div className="field">
            <label htmlFor="entryRule">くじ参加回数ルール</label>
            <select
              id="entryRule"
              value={event.entryRule}
              onChange={(e) => setEvent({ ...event, entryRule: e.target.value as EventInput['entryRule'] })}
            >
              <option value="ONCE_PER_DAY">1日1回</option>
              <option value="ONCE_PER_PERIOD">期間中1回</option>
              <option value="UNLIMITED">複数回参加可</option>
            </select>
          </div>

          <div className="field">
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                style={{ width: 'auto' }}
                checked={event.requireFriendForReward}
                onChange={(e) => setEvent({ ...event, requireFriendForReward: e.target.checked })}
              />
              特典受取に公式LINE友だち登録を必須にする
            </label>
          </div>

          <h2>確認</h2>
          <table>
            <tbody>
              <tr>
                <th>イベント名</th>
                <td>{event.name || '未入力'}</td>
              </tr>
              <tr>
                <th>期間</th>
                <td>
                  {new Date(event.startAt).toLocaleString('ja-JP')} 〜 {new Date(event.endAt).toLocaleString('ja-JP')}
                </td>
              </tr>
              <tr>
                <th>対象店舗</th>
                <td>
                  CocodePit {filledPits.length} 台
                  {pitsWithoutStamp.length > 0 &&
                    `（スタンプコード未登録 ${pitsWithoutStamp.length} 台。公開前に登録が必要です）`}
                </td>
              </tr>
              <tr>
                <th>抽選タイプ</th>
                <td>{event.lotteryType === 'SCRATCH' ? 'スクラッチ' : 'ルーレット'}</td>
              </tr>
              <tr>
                <th>賞</th>
                <td>
                  {prizes.map((p) => `${p.name}（${p.isConsolation ? '参加賞' : `${p.stock ?? '無制限'}本`}）`).join(' / ')}
                </td>
              </tr>
            </tbody>
          </table>

          <div className="actions">
            <button type="button" className="btn" onClick={() => void save()} disabled={busy}>
              {busy ? '保存中…' : isEdit ? '保存する' : '作成する'}
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => navigate('/events')}>
              キャンセル
            </button>
          </div>
        </div>
      )}

      <div className="actions">
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
        >
          戻る
        </button>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
          disabled={step === STEPS.length - 1}
        >
          次へ
        </button>
      </div>
    </>
  );
}

interface ImageFieldProps {
  label: string;
  value: string | null;
  disabled?: boolean;
  onPick: (file: File) => void;
  onClear: () => void;
}

function ImageField({ label, value, disabled, onPick, onClear }: ImageFieldProps): JSX.Element {
  return (
    <div className="field">
      <label>{label}</label>
      {value && (
        <div style={{ marginBottom: 8 }}>
          <img src={value} alt="" style={{ maxWidth: 240, borderRadius: 8, display: 'block' }} />
          <button type="button" className="btn btn--ghost" style={{ marginTop: 6 }} onClick={onClear} disabled={disabled}>
            画像を外す
          </button>
        </div>
      )}
      <input
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        disabled={disabled}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onPick(file);
        }}
      />
    </div>
  );
}
